/**
 * @fileoverview SVG bar chart for distribution data with optional stacking
 * @module ctl-bar-chart
 * @version 1.0.0
 *
 * @description
 * Renders a responsive SVG bar chart. Supports grouped and stacked layouts,
 * horizontal orientation, a legend, and interactive tooltips.
 * Listens for `ctl-date-range-change` events from the page to allow
 * external date-range filtering.
 *
 * @example
 * <ctl-bar-chart
 *   title="Page Views by Source"
 *   x-labels='["Direct","Organic","Paid","Social","Referral"]'
 *   series='[{"label":"Desktop","color":"#6200ee","values":[420,310,180,95,60]},
 *             {"label":"Mobile","color":"#03dac6","values":[280,240,155,210,85]}]'
 *   stacked>
 * </ctl-bar-chart>
 */

import { safeJsonParse } from '../../../utils/safeJsonParse.js';
import "../foundation/ctl-skeleton.js";

const PAD = { top: 16, right: 20, bottom: 52, left: 56 };
const DEFAULT_COLORS = [
  "#6200ee", "#03dac6", "#f59e0b", "#10b981", "#ef4444",
  "#3b82f6", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"
];

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host {
    display: block;
    font-family: var(--ctl-font-sans, system-ui, -apple-system, Segoe UI, Roboto, Arial);
    color: var(--ctl-color-text);
  }

  .card {
    background: var(--ctl-color-surface);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    box-shadow: var(--ctl-shadow-card);
    overflow: hidden;
  }

  .head {
    padding: var(--ctl-space-3-5) var(--ctl-space-4);
    border-bottom: 1px solid var(--ctl-color-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--ctl-space-3);
    flex-wrap: wrap;
  }

  .title {
    font-size: var(--ctl-font-size-lg);
    font-weight: var(--ctl-font-weight-heavy);
    line-height: 1.2;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ctl-space-3);
    align-items: center;
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
    font-size: var(--ctl-font-size-sm);
    color: var(--ctl-color-muted);
    cursor: pointer;
    user-select: none;
  }

  .legend-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .chart-wrap {
    padding: var(--ctl-space-3) var(--ctl-space-2) var(--ctl-space-2);
  }

  svg {
    display: block;
    width: 100%;
    overflow: visible;
  }

  .grid-line { stroke: var(--ctl-color-border); stroke-dasharray: 4 4; }
  .axis-line { stroke: var(--ctl-color-border); }
  .axis-label { fill: var(--ctl-color-muted); font-size: 11px; }

  .bar {
    cursor: pointer;
    transition: opacity 120ms;
  }
  .bar:hover { opacity: 0.82; }

  .tooltip {
    position: absolute;
    background: var(--ctl-color-surface-inverse);
    color: var(--ctl-color-on-surface-inverse);
    border-radius: var(--ctl-radius-md);
    padding: var(--ctl-space-2) var(--ctl-space-3);
    font-size: var(--ctl-font-size-sm);
    pointer-events: none;
    white-space: nowrap;
    box-shadow: var(--ctl-shadow-dropdown);
    opacity: 0;
    transition: opacity 100ms;
    z-index: 10;
  }

  .tooltip.visible { opacity: 1; }

  .wrap {
    position: relative;
  }

  :host([loading]) .real { display: none; }
  .loading { display: none; padding: var(--ctl-space-4); }
  :host([loading]) .loading { display: block; }
</style>

<div class="card" part="card">
  <div class="head" part="head">
    <div class="title" id="title"></div>
    <div class="legend" id="legend" part="legend"></div>
  </div>
  <div class="real">
    <div class="wrap" part="wrap">
      <div class="chart-wrap" part="chart-wrap">
        <svg id="svg" role="img" aria-label="Bar chart"></svg>
      </div>
      <div class="tooltip" id="tooltip"></div>
    </div>
  </div>
  <div class="loading" part="loading" aria-hidden="true">
    <ctl-skeleton variant="rect" height="220px"></ctl-skeleton>
  </div>
</div>
`;

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

export class CtlBarChart extends HTMLElement {
  static get observedAttributes() {
    return ["title", "x-labels", "series", "stacked", "horizontal", "loading", "y-label", "x-label", "height"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._titleEl = this.shadowRoot.getElementById("title");
    this._legendEl = this.shadowRoot.getElementById("legend");
    this._svg = this.shadowRoot.getElementById("svg");
    this._tooltip = this.shadowRoot.getElementById("tooltip");
    this._series = [];
    this._xLabels = [];
    this._hidden = new Set();
    this._onRangeChange = this._onRangeChange.bind(this);
    this._onResize = this._onResize.bind(this);
    this._ro = new ResizeObserver(this._onResize);
  }

  connectedCallback() {
    this._parse();
    this._bindEvents();
    this._render();
    this._ro.observe(this);
    this.getRootNode().addEventListener("ctl-date-range-change", this._onRangeChange);
  }

  disconnectedCallback() {
    this._ro.unobserve(this);
    this.getRootNode().removeEventListener("ctl-date-range-change", this._onRangeChange);
  }

  /** Bind all interactive events once using delegation. */
  _bindEvents() {
    // Legend: toggle series visibility
    this._legendEl.addEventListener("click", (e) => {
      const item = e.target.closest(".legend-item");
      if (!item) return;
      const idx = parseInt(item.dataset.seriesIdx, 10);
      if (Number.isNaN(idx)) return;
      if (this._hidden.has(idx)) this._hidden.delete(idx);
      else this._hidden.add(idx);
      this._render();
    });
    this._legendEl.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const item = e.target.closest(".legend-item");
      if (!item) return;
      e.preventDefault();
      const idx = parseInt(item.dataset.seriesIdx, 10);
      if (Number.isNaN(idx)) return;
      if (this._hidden.has(idx)) this._hidden.delete(idx);
      else this._hidden.add(idx);
      this._render();
    });

    // Bars: tooltip via mouseover / mouseout / focusin / focusout
    this._svg.addEventListener("mouseover", (e) => {
      const bar = e.target.closest(".bar");
      if (!bar) return;
      const from = e.relatedTarget instanceof Element ? e.relatedTarget.closest(".bar") : null;
      if (from === bar) return;
      this._showTooltip(e, bar.dataset.name, bar.dataset.label, bar.dataset.value);
    });
    this._svg.addEventListener("mouseout", (e) => {
      const bar = e.target.closest(".bar");
      if (!bar) return;
      const to = e.relatedTarget instanceof Element ? e.relatedTarget.closest(".bar") : null;
      if (to === bar) return;
      this._hideTooltip();
    });
    this._svg.addEventListener("focusin", (e) => {
      const bar = e.target.closest(".bar");
      if (!bar) return;
      this._showTooltip(e, bar.dataset.name, bar.dataset.label, bar.dataset.value);
    });
    this._svg.addEventListener("focusout", (e) => {
      const bar = e.target.closest(".bar");
      if (!bar) return;
      this._hideTooltip();
    });
  }

  attributeChangedCallback(name) {
    if (name === "x-labels" || name === "series") this._parse();
    this._render();
  }

  get title() { return this.getAttribute("title") || ""; }
  set title(v) { this.setAttribute("title", String(v ?? "")); }

  get stacked() { return this.hasAttribute("stacked"); }
  set stacked(v) { v ? this.setAttribute("stacked", "") : this.removeAttribute("stacked"); }

  get horizontal() { return this.hasAttribute("horizontal"); }
  set horizontal(v) { v ? this.setAttribute("horizontal", "") : this.removeAttribute("horizontal"); }

  get loading() { return this.hasAttribute("loading"); }
  set loading(v) { v ? this.setAttribute("loading", "") : this.removeAttribute("loading"); }

  get series() { return this._series; }
  set series(v) {
    this._series = Array.isArray(v) ? v : [];
    this.setAttribute("series", JSON.stringify(this._series));
  }

  get xLabels() { return this._xLabels; }
  set xLabels(v) {
    this._xLabels = Array.isArray(v) ? v : [];
    this.setAttribute("x-labels", JSON.stringify(this._xLabels));
  }

  _parse() {
    const raw = this.getAttribute("series");
    if (raw) this._series = safeJsonParse(raw, []);
    const lraw = this.getAttribute("x-labels");
    if (lraw) this._xLabels = safeJsonParse(lraw, []);
  }

  _onRangeChange(e) {
    const { start, end } = e.detail || {};
    if (!start || !end) return;
    // Re-render when the date range changes; consumers can also filter
    // the series data externally and update the `series` property.
    this._render();
  }

  _onResize() { this._render(); }

  _visibleSeries() {
    return this._series.map((s, i) => ({
      ...s,
      color: s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      visible: !this._hidden.has(i)
    }));
  }

  _render() {
    this._titleEl.textContent = this.title;
    if (this.loading) return;

    const allSeries = this._visibleSeries();
    this._renderLegend(allSeries);

    const series = allSeries.filter(s => s.visible);
    if (series.length === 0) { this._svg.innerHTML = ""; return; }

    const wrap = this.shadowRoot.querySelector(".chart-wrap");
    const W = wrap.clientWidth || 600;
    const H = parseInt(this.getAttribute("height") || "260", 10);
    const svg = this._svg;
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    svg.setAttribute("height", H);
    svg.setAttribute("aria-label", `Bar chart: ${this.title}`);

    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const xLabels = this._xLabels;
    const groupCount = xLabels.length || Math.max(...series.map(s => (s.values || []).length), 0);
    if (groupCount === 0) { svg.innerHTML = ""; return; }

    const stacked = this.stacked;

    // Compute max value
    let yMax = 0;
    if (stacked) {
      for (let i = 0; i < groupCount; i++) {
        const sum = series.reduce((acc, s) => acc + (s.values?.[i] || 0), 0);
        yMax = Math.max(yMax, sum);
      }
    } else {
      series.forEach(s => (s.values || []).forEach(v => { yMax = Math.max(yMax, v || 0); }));
    }
    const yNice = this._niceMax(yMax);

    const yScale = (v) => PAD.top + innerH - (v / yNice) * innerH;
    const yTicks = 5;

    let html = "";

    // Grid + Y axis labels
    for (let t = 0; t <= yTicks; t++) {
      const yv = (yNice / yTicks) * t;
      const y = yScale(yv);
      html += `<line class="grid-line" x1="${PAD.left}" y1="${y}" x2="${PAD.left + innerW}" y2="${y}"/>`;
      html += `<text class="axis-label" x="${PAD.left - 6}" y="${y + 4}" text-anchor="end">${this._formatY(yv)}</text>`;
    }

    // Axis lines
    html += `<line class="axis-line" x1="${PAD.left}" y1="${PAD.top}" x2="${PAD.left}" y2="${PAD.top + innerH}"/>`;
    html += `<line class="axis-line" x1="${PAD.left}" y1="${PAD.top + innerH}" x2="${PAD.left + innerW}" y2="${PAD.top + innerH}"/>`;

    // Axis titles
    const xTitle = this.getAttribute("x-label");
    const yTitle = this.getAttribute("y-label");
    if (xTitle) {
      html += `<text class="axis-label" x="${PAD.left + innerW / 2}" y="${H - 4}" text-anchor="middle">${this._esc(xTitle)}</text>`;
    }
    if (yTitle) {
      html += `<text class="axis-label" x="12" y="${PAD.top + innerH / 2}" text-anchor="middle" transform="rotate(-90, 12, ${PAD.top + innerH / 2})">${this._esc(yTitle)}</text>`;
    }

    const groupW = innerW / groupCount;
    const BAR_GAP = 0.15; // fraction of groupW reserved as gap between groups
    const barGroupW = groupW * (1 - BAR_GAP);

    if (stacked) {
      for (let gi = 0; gi < groupCount; gi++) {
        const gx = PAD.left + gi * groupW + groupW * BAR_GAP / 2;
        let stackY = PAD.top + innerH;
        series.forEach((s, si) => {
          const v = s.values?.[gi] || 0;
          if (v === 0) return;
          const barH = (v / yNice) * innerH;
          const by = stackY - barH;
          const rx = Math.min(4, barGroupW / 3);
          const label = xLabels[gi] != null ? String(xLabels[gi]) : String(gi + 1);
          html += `<rect class="bar"
            x="${gx.toFixed(1)}"
            y="${by.toFixed(1)}"
            width="${barGroupW.toFixed(1)}"
            height="${barH.toFixed(1)}"
            rx="${rx}"
            fill="${s.color}"
            data-name="${this._esc(s.label || "")}"
            data-label="${this._esc(label)}"
            data-value="${v}"
            tabindex="0"
            role="img"
            aria-label="${this._esc(s.label || "")}: ${v} at ${this._esc(label)}"
          />`;
          stackY = by;
        });
        const label = xLabels[gi] != null ? String(xLabels[gi]) : String(gi + 1);
        html += `<text class="axis-label" x="${(gx + barGroupW / 2).toFixed(1)}" y="${PAD.top + innerH + 18}" text-anchor="middle">${this._esc(label)}</text>`;
      }
    } else {
      // Grouped
      const seriesCount = series.length;
      const barW = barGroupW / seriesCount;
      for (let gi = 0; gi < groupCount; gi++) {
        const groupX = PAD.left + gi * groupW + groupW * BAR_GAP / 2;
        series.forEach((s, si) => {
          const v = s.values?.[gi] || 0;
          const bx = groupX + si * barW;
          const barH = (v / yNice) * innerH;
          const by = yScale(v);
          const rx = Math.min(4, barW / 3);
          const label = xLabels[gi] != null ? String(xLabels[gi]) : String(gi + 1);
          html += `<rect class="bar"
            x="${bx.toFixed(1)}"
            y="${by.toFixed(1)}"
            width="${Math.max(1, barW - 2).toFixed(1)}"
            height="${barH.toFixed(1)}"
            rx="${rx}"
            fill="${s.color}"
            data-name="${this._esc(s.label || "")}"
            data-label="${this._esc(label)}"
            data-value="${v}"
            tabindex="0"
            role="img"
            aria-label="${this._esc(s.label || "")}: ${v} at ${this._esc(label)}"
          />`;
        });
        const label = xLabels[gi] != null ? String(xLabels[gi]) : String(gi + 1);
        html += `<text class="axis-label" x="${(groupX + barGroupW / 2).toFixed(1)}" y="${PAD.top + innerH + 18}" text-anchor="middle">${this._esc(label)}</text>`;
      }
    }

    svg.innerHTML = html;
  }

  /** Update legend HTML only; event handling is delegated via _bindEvents(). */
  _renderLegend(series) {
    this._legendEl.innerHTML = series.map((s, i) => `
      <div class="legend-item" data-series-idx="${i}" role="checkbox" aria-checked="${s.visible}" tabindex="0">
        <div class="legend-swatch" style="background:${s.color};opacity:${s.visible ? 1 : 0.3};"></div>
        <span style="opacity:${s.visible ? 1 : 0.4}">${this._esc(s.label || `Series ${i + 1}`)}</span>
      </div>
    `).join("");
  }

  _showTooltip(e, name, label, value) {
    const tip = this._tooltip;
    tip.innerHTML = `<strong>${this._esc(name)}</strong><br>${this._esc(label)}: ${this._esc(this._formatY(parseFloat(value)))}`;
    tip.classList.add("visible");
    const wrap = this.shadowRoot.querySelector(".wrap");
    const rect = wrap.getBoundingClientRect();
    const ex = e.clientX - rect.left + 12;
    const ey = e.clientY - rect.top - 36;
    tip.style.left = `${clamp(ex, 0, rect.width - 140)}px`;
    tip.style.top = `${clamp(ey, 0, rect.height - 60)}px`;
  }

  _hideTooltip() { this._tooltip.classList.remove("visible"); }

  _niceMax(v) {
    if (!v || v <= 0) return 10;
    const exp = Math.pow(10, Math.floor(Math.log10(v)));
    const frac = v / exp;
    const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
    return nice * exp;
  }

  _formatY(v) {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
    return String(Math.round(v));
  }

  _esc(s) {
    return String(s ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }
}

if (!customElements.get("ctl-bar-chart")) {
  customElements.define("ctl-bar-chart", CtlBarChart);
}
